import { useEffect, useRef } from 'react';

import type { SocialOpportunityItem } from '@inference.engine/widgets/web';

import type { OpportunityItemWithFavorite } from '../../../ui/components/OpportunitiesList/OpportunitiesList.types';
import { useSocialMediaOpportunitiesStore } from '../../social-media-opportunities-widget.store';

interface UseOpportunitySelectionParams {
  opportunities: OpportunityItemWithFavorite[] | null;
  dataItems: SocialOpportunityItem[];
  dataSelectedItem: SocialOpportunityItem | undefined;
}

interface UseOpportunitySelectionReturn {
  selectedItem: SocialOpportunityItem | null;
  detailsView: boolean;
  clearPersistedSelection: () => void;
}

export function useOpportunitySelection(
  params: UseOpportunitySelectionParams,
): UseOpportunitySelectionReturn {
  const { opportunities, dataItems, dataSelectedItem } = params;

  // Store selectors
  const detailsView = useSocialMediaOpportunitiesStore((state) => state.detailsView);
  const selectedItem = useSocialMediaOpportunitiesStore((state) => state.selectedItem);
  const persistedSelectionTitle = useSocialMediaOpportunitiesStore(
    (state) => state.persistedSelectionTitle,
  );
  const setDetailsView = useSocialMediaOpportunitiesStore((state) => state.setDetailsView);
  const setSelectedItem = useSocialMediaOpportunitiesStore((state) => state.setSelectedItem);
  const clearPersistedSelection = useSocialMediaOpportunitiesStore(
    (state) => state.clearPersistedSelection,
  );

  // Initialize selection from persisted state on mount
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    if (persistedSelectionTitle && dataItems.length > 0) {
      const restoredItem = dataItems.find(
        (item) => item.opportunityTitle === persistedSelectionTitle,
      );
      if (restoredItem) {
        setSelectedItem(restoredItem);
        setDetailsView(true);
      }
    }
  }, [persistedSelectionTitle, dataItems, setSelectedItem, setDetailsView]);

  // Clear stale persisted state on fresh session (no pre-selected item, no initial data).
  // Separate from mount init because the component may not unmount between sessions.
  useEffect(() => {
    if (!dataSelectedItem && dataItems.length === 0) {
      clearPersistedSelection();
    }
  }, [dataSelectedItem, dataItems.length, clearPersistedSelection]);

  // Initialize with preselected item if provided in data
  useEffect(() => {
    if (dataSelectedItem) {
      setSelectedItem(dataSelectedItem);
      setDetailsView(true);
    }
  }, [dataSelectedItem, setSelectedItem, setDetailsView]);

  // Try to restore selection from persisted state when opportunities become available
  useEffect(() => {
    if (!selectedItem && persistedSelectionTitle && opportunities && opportunities.length > 0) {
      const restoredItem = opportunities.find(
        (item) => item.opportunityTitle === persistedSelectionTitle,
      );
      if (restoredItem) {
        setSelectedItem(restoredItem);
        setDetailsView(true);
      }
    }
  }, [opportunities, selectedItem, persistedSelectionTitle, setSelectedItem, setDetailsView]);

  return {
    selectedItem,
    detailsView,
    clearPersistedSelection,
  };
}
